import { IButtonStyles } from '@fluentui/react/lib/Button';
import { IContextualMenuStyleProps, IContextualMenuStyles } from '@fluentui/react/lib/ContextualMenu';

import { ComponentClassNames } from '../../util/classNames';
import { HighContrastContextualMenuStyles } from '../../util/styles/highContrastEmulation';
import { IStyleFunction, ITheme, memoizeFunction, mergeStyleSets } from '../../util/styling';
import { ButtonColor } from '../Button';
import { getButtonColors } from '../Button/Button.styles';

const emptyStylesObject = {};

type MenuButtonClassNames = ComponentClassNames<'menuButton'>;
export const getClassNames = memoizeFunction(
  (className?: string): MenuButtonClassNames =>
    mergeStyleSets({
      menuButton: [
        {
          display: 'inline-block',
        },
        className,
      ],
    })
);

export const getStyles = memoizeFunction(
  (
    theme: ITheme,
    size: number,
    hasBorder: boolean,
    useTransparentBackground: boolean,
    color: ButtonColor
  ): IButtonStyles => {
    const iconSize = size / 2;

    const borderProps = hasBorder
      ? {
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme.palette.neutralLight,
        }
      : {};

    const { root, rootHovered, rootPressed, rootDisabled } = getButtonColors(color, theme);
    const rootColors = useTransparentBackground
      ? { ...(root as Record<string, unknown>), backgroundColor: 'transparent' }
      : (root as Record<string, unknown>);

    const styles: IButtonStyles = {
      root: {
        height: size,
        width: size,
        minWidth: size,
        maxWidth: size,
        ...rootColors,
        ...borderProps,
      },
      rootHovered: useTransparentBackground ? emptyStylesObject : rootHovered,
      rootPressed: useTransparentBackground ? emptyStylesObject : rootPressed,
      rootDisabled: useTransparentBackground ? emptyStylesObject : rootDisabled,
      icon: {
        height: iconSize,
        lineHeight: iconSize,
        fontSize: iconSize,
        display: 'flex',
        alignItems: 'center',
      },
      menuIcon: {
        display: 'none',
      },
      flexContainer: {
        flexDirection: 'column',
      },
    };

    return mergeStyleSets(styles);
  }
);

const iconAlignmentStyles = {
  minWidth: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

type SVGIconClassNames = ComponentClassNames<'icon'>;

export const getClassNamesForSVGIcon = memoizeFunction(
  (iconSize: number): SVGIconClassNames =>
    mergeStyleSets({
      icon: {
        margin: '0 4px',
        fontSize: iconSize,
        ...iconAlignmentStyles,
      },
    })
);

export const menuStyles: Partial<IStyleFunction<IContextualMenuStyleProps, IContextualMenuStyles>> = {
  ...HighContrastContextualMenuStyles,
  list: {
    ...HighContrastContextualMenuStyles.list,
    selectors: {
      ...HighContrastContextualMenuStyles.list.selectors,
      '.ms-ContextualMenu-icon': {
        ...iconAlignmentStyles,
        width: 'fit-content',
        '.y-fluent-icon': {
          display: 'flex',
        },
      },
    },
  },
};
